import type { StoreApi } from 'zustand'
import type { AppState } from '@/store/rootTypes'
import { createEmptyArchiveStats } from '@/store/retention'
import type { StudentAccount } from '@/entities/student-workspace'
import { activeWorkspacePatch, createEmptyWorkspace } from '@/store/workspace'
import { getPreferredLearningStyle } from '@/entities/profile/getPreferredLearningStyle'
import { getPreferredLearnerType } from '@/entities/profile/getSocialLearningProfile'
import { resolveLearnerType } from '@/data/learnerTypeConfig'
import { createInitialProfile } from '@/features/pretest'
import { LocalStorageService, STORAGE_KEYS } from '@/services/storage'
import { layerLessonConfigs } from '@/data/lessonEngine'
import { getStarterOutfit } from '@/data/outfitConfig'
import { resolveEffectiveLearningLayer } from '@/store/effectiveLearningLayer'
import { createLearningStage } from '@/entities/stage'

const profileStorage = new LocalStorageService()
type SetState = StoreApi<AppState>['setState']
type GetState = StoreApi<AppState>['getState']

export function createProfileSlice(set: SetState, get: GetState): Pick<AppState,
  'setProfile' | 'setInitialProfile' | 'clearProfile' | 'setStudent' | 'setGrade' |
  'setPretestResult' | 'setLearningStyle' | 'setLearnerType' | 'setManualLayerOverride' |
  'setDynamicLayer' | 'getEffectiveLayer' | 'getLessonConfig'> {
  return {
    setProfile: (profile) => {
      const style = getPreferredLearningStyle(profile.preference)
      const recommendedLearnerType = getPreferredLearnerType(profile.preference)
      profileStorage.remove(STORAGE_KEYS.profile)
      set((state) => {
        const learnerChanged = Boolean(state.profile?.identity.id && state.profile.identity.id !== profile.identity.id)
        const activeStudentId = state.activeStudentId ?? profile.identity.id
        const now = new Date().toISOString()
        const existingAccount = state.studentIndex.find((item) => item.id === activeStudentId)
        const account: StudentAccount = {
          id: activeStudentId,
          name: profile.identity.name,
          grade: profile.identity.grade,
          avatar: profile.identity.avatar,
          hasCompletedPretest: true,
          createdAt: existingAccount?.createdAt ?? now,
          updatedAt: now,
        }
        const learnerType = state.workspaces[activeStudentId]?.learnerType ?? recommendedLearnerType
        const starterOutfit = getStarterOutfit(learnerType)
        const existingWorkspace = state.workspaces[activeStudentId]
        const firstCompletedPretest = !existingWorkspace?.profile
        const currentStage = existingWorkspace?.currentStage ?? createLearningStage({
          layer: profile.recommendedLevel,
          source: 'pretest',
        })
        const currentOutfit = firstCompletedPretest ? starterOutfit : existingWorkspace?.currentOutfit ?? state.currentOutfit
        const unlockedOutfits = starterOutfit === 'none'
          ? existingWorkspace?.unlockedOutfits ?? state.unlockedOutfits
          : Array.from(new Set([...(existingWorkspace?.unlockedOutfits ?? state.unlockedOutfits), starterOutfit]))
        const ownedOutfits = starterOutfit === 'none'
          ? existingWorkspace?.ownedOutfits ?? state.ownedOutfits
          : Array.from(new Set([...(existingWorkspace?.ownedOutfits ?? state.ownedOutfits), starterOutfit]))
        return {
          activeStudentId,
          studentIndex: existingAccount
            ? state.studentIndex.map((item) => item.id === activeStudentId ? account : item)
            : [...state.studentIndex, account],
          workspaces: {
            ...state.workspaces,
            [activeStudentId]: {
              ...(state.workspaces[activeStudentId] ?? createEmptyWorkspace(account)),
              profile,
              student: {
                ...(state.workspaces[activeStudentId]?.student ?? state.student),
                name: profile.identity.name,
                grade: profile.identity.grade,
                avatar: profile.identity.avatar,
                layer: profile.recommendedLevel,
                style,
              },
              currentGrade: profile.identity.grade,
              pretestScore: profile.pretestScore,
              pretestLayer: profile.recommendedLevel,
              currentStage,
              learningStyle: style,
              learnerType,
              currentOutfit,
              unlockedOutfits,
              ownedOutfits,
            },
          },
          profile,
          student: {
            ...state.student,
            name: profile.identity.name,
            grade: profile.identity.grade,
            avatar: profile.identity.avatar,
            layer: profile.recommendedLevel,
            style,
          },
          currentGrade: profile.identity.grade,
          pretestScore: profile.pretestScore,
          pretestLayer: profile.recommendedLevel,
          currentStage,
          learningStyle: style,
          learnerType,
          currentOutfit,
          unlockedOutfits,
          ownedOutfits,
          ...(learnerChanged ? { currentPlan: null, currentPlanKey: null, attempts: [], currentReport: null } : {}),
        }
      })
    },
    setInitialProfile: (identity, result) => {
      const activeStudentId = get().activeStudentId ?? identity.id
      if (get().workspaces[activeStudentId]?.profile) return false
      get().setProfile(createInitialProfile(identity, result))
      return true
    },
    clearProfile: () => {
      profileStorage.remove(STORAGE_KEYS.profile)
      set((state) => {
        const student = { ...state.student, name: '小探险家', grade: 'G3' as const, avatar: '🦊', layer: null, style: null }
        const patch = {
          profile: null,
          student,
          currentGrade: 'all' as const,
          pretestScore: null,
          pretestLayer: null,
          learningStyle: null,
          learnerType: null,
          manualLayerOverride: null,
          dynamicLayer: null,
          currentStage: null,
          currentPlan: null,
          currentPlanKey: null,
          attempts: [],
          currentReport: null,
          learningEvents: [],
          archiveStats: createEmptyArchiveStats(),
        }
        return {
          ...patch,
          ...activeWorkspacePatch(state, patch),
          studentIndex: state.activeStudentId
            ? state.studentIndex.map((item) => item.id === state.activeStudentId
              ? { ...item, hasCompletedPretest: false, updatedAt: new Date().toISOString() }
              : item)
            : state.studentIndex,
        }
      })
    },
    setStudent: (student) => set((state) => {
      const nextStudent = { ...state.student, ...student }
      return { student: nextStudent, ...activeWorkspacePatch(state, { student: nextStudent }) }
    }),
    setGrade: (grade) => set((state) => ({ currentGrade: grade, ...activeWorkspacePatch(state, { currentGrade: grade }) })),
    setPretestResult: (score, layer) => set((state) => {
      const student = { ...state.student, layer }
      const currentStage = createLearningStage({ layer, source: 'pretest' })
      const patch = { pretestScore: score, pretestLayer: layer, student, currentStage }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    setLearningStyle: (style) => set((state) => {
      const learnerType = resolveLearnerType(style)
      const student = { ...state.student, style }
      const patch = { learningStyle: style, learnerType, student }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    setLearnerType: (learnerType) => set((state) => ({
      learnerType,
      ...activeWorkspacePatch(state, { learnerType }),
    })),
    setManualLayerOverride: (layer) => set((state) => ({
      manualLayerOverride: layer,
      ...activeWorkspacePatch(state, { manualLayerOverride: layer }),
    })),
    setDynamicLayer: (layer) => set((state) => ({
      dynamicLayer: layer,
      ...activeWorkspacePatch(state, { dynamicLayer: layer }),
    })),
    getEffectiveLayer: () => {
      const state = get()
      return resolveEffectiveLearningLayer(state)
    },
    getLessonConfig: () => layerLessonConfigs[get().getEffectiveLayer()],
  }
}
